/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  BUTTON_TYPE_SUBMIT,
  Button,
  FORM_LAYOUT_HORIZONTAL,
  FORM_THEME_DARK,
  Form,
  SelectField,
  SelectFieldOption,
  SelectFieldProps,
} from '@squiz/sds';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CDP_STRINGS } from '../../../../Constants/Strings.constants';
import { CUSTOMERS_DATA_IDS, CUSTOMERS_FORM_IDS } from '../../SegmentInsights.constants';
import { SegmentPickerProps } from './SegmentPicker.props';

/**
 * Select a segment from an available list to view customer data.
 * @param {SegmentPickerProps} props Props passed to `<SegmentPicker />`.
 * @export
 * @returns {JSX.Element} The `<SegmentPicker />` component.
 */
export const SegmentPicker = (props: SegmentPickerProps): JSX.Element => {
  const { t } = useTranslation();
  const { activeSegment, segments, setActiveSegment } = props;

  const defaultSegmentCode = activeSegment?.code || null;
  const [selectedSegment, setSelectedSegment] = useState<string | null>(defaultSegmentCode);

  const segmentRef = useRef<HTMLInputElement>(null);

  const labels = useMemo(() => {
    return {
      apply: t(CDP_STRINGS.apply),
      description: t(CDP_STRINGS.segmentPickerDescription),
      segmentPicker: t(CDP_STRINGS.segmentPicker),
      segmentSelect: t(CDP_STRINGS.segmentSelect),
    };
  }, [t]);

  const applyHandler = (): void => {
    const segment = segments.find((segment) => segment.code === selectedSegment);
    segment && setActiveSegment(segment);
  };

  const selectFieldProps: SelectFieldProps = {
    callback: (option: SelectFieldOption): void => {
      setSelectedSegment(option.value);
    },
    fieldLabel: labels.segmentSelect,
    id: CUSTOMERS_FORM_IDS.segment,
    name: CUSTOMERS_FORM_IDS.segment,
    options: segments.map((segment): SelectFieldOption => {
      return {
        key: segment.code,
        label: segment.name,
        value: segment.code,
      };
    }),
    placeholder: labels.segmentSelect,
    value: selectedSegment || '',
  };

  // Disable apply button if there is no segment selected, OR
  // if the selected segment is the same as the already active segment
  const applyDisabled = !selectedSegment || selectedSegment === activeSegment?.code;
  const applyButton = (
    <Button
      data-id={CUSTOMERS_DATA_IDS.segmentPickerApply}
      disabled={applyDisabled}
      isWide
      onClick={applyHandler}
      text={labels.apply}
      type={BUTTON_TYPE_SUBMIT}
    />
  );

  return (
    <>
      <div className="segment-picker__description">
        <span>{labels.description}</span>
      </div>
      <Form
        buttonKeys={[`apply-button`]}
        buttons={[applyButton]}
        classes="segment-picker__form"
        layout={FORM_LAYOUT_HORIZONTAL}
        legend={labels.segmentPicker}
        theme={FORM_THEME_DARK}
      >
        <SelectField {...selectFieldProps} data-id={CUSTOMERS_DATA_IDS.segmentPickerSelect} ref={segmentRef} />
      </Form>
    </>
  );
};
